<script lang="ts" setup>
import { computed, defineComponent, defineEmits, onMounted, ref } from 'vue'
import { onBeforeRouteUpdate } from 'vue-router'

import { cancelMembershipBase } from '__generated__/cancelMembershipBase'
import { newMembershipBase } from '__generated__/newMembershipBase'
import { updateUser } from '__generated__/updateUser'

import { Background } from '@base/_types/Background'
import { ToastSemantic } from '@base/_types/Toast'

import { useClient } from '@base/api/useClient'
import { CANCEL_MEMBERSHIP_MUTATION } from '@base/api/mutations/cancelMembership'
import { NEW_MEMBERSHIP_MUTATION } from '@base/api/mutations/newMembership'
import { UPDATE_USER } from '@base/api/mutations/updateUser'

import { stripeElements } from '@base/lib/stripeElements'

import { useToastsStore } from '@base/store/toastsStore'
import { useUserStore } from '@base/store/userStore'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import USAvatar from '@base/components/USAvatar/USAvatar.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'

import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import blobBg from '@player/users/assets/register-step-1-blob-bg.svg'
import { load } from 'dotenv'

defineComponent({
  name: 'RegisterStep4'
})

const emits = defineEmits(['next'])

const client = useClient()
const userStore = useUserStore()
const toastsStore = useToastsStore()

const stripeClientSecret = ref('')

let stripe
let elements
let paymentElement

const golfCanadaMembershipId = ref('')
const golfCanadaMembershipIdError = ref('')
const loading = ref<boolean>(false)

const generateInitials = computed(() => {
  return `${userStore.firstName.charAt(0).toUpperCase()}${userStore.lastName
    .charAt(1)
    .toUpperCase()}`
})

const bgImage: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: blobBg
        }
      }
    }
  ]
}

const sendToast = (msg: string, state: ToastSemantic) => {
  toastsStore.pushToast({
    content: msg,
    icon: state == ToastSemantic.Error ? 'exclamation-square' : 'check-circle',
    semantic: state
  })
}

const reset = () => {
  stripeClientSecret.value = ''
}

const createMembership = async () => {
  try {
    const { data } = await client.mutate<newMembershipBase>({
      mutation: NEW_MEMBERSHIP_MUTATION,
      variables: {
        input: {
          userId: userStore.id
        }
      }
    })

    if (
      data &&
      data.newMembership.__typename === 'NewMembershipOutput' &&
      data.newMembership.clientSecret !== null
    ) {
      stripeClientSecret.value = data.newMembership.clientSecret
    }
  } catch (e) {
    toastsStore.pushToast({
      content: 'There was an error processing that request',
      icon: 'exclamation-square',
      semantic: ToastSemantic.Error
    })
  }
}

onMounted(async () => {
  try {
    reset()
    await createMembership()
    /*
    console.log(stripeClientSecret.value)
    console.log(userStore.id)
    ;[stripe, elements] = stripeElements(
      stripeClientSecret.value,
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
    )
    paymentElement = elements.create('payment', {
      terms: {
        card: 'auto'
      }
    })
    paymentElement.mount('#stripe-element')
    */
  } catch (e) {
    console.error(e)
    // toastsStore.pushToast({
    //   content: 'Something went wrong',
    //   icon: 'exclamation-square',
    //   semantic: ToastSemantic.Error
    // })
  }
})

const confirmPaymentIntent = () => {
  try {
    stripe
      .confirmPayment({
        elements,
        confirmParams: {
          return_url: 'http://localhost:3000#success'
        },
        redirect: 'if_required'
      })
      .then((result) => {
        if (result.error) {
          console.log(`confirmPaymentIntent error: ${result.error}`)
          // toastsStore.pushToast({
          //   content: 'There was an error with your payment',
          //   icon: 'exclamation-square',
          //   semantic: ToastSemantic.Error
          // })
          return
        }
        userStore.membership.active = true
        emits('next')
      })
  } catch (e) {
    console.error(e)
    // toastsStore.pushToast({
    //   content: 'There was an error with your payment',
    //   icon: 'exclamation-square',
    //   semantic: ToastSemantic.Error
    // })
  }
}

const handleCancelMembership = async () => {
  try {
    const { data } = await client.mutate<cancelMembershipBase>({
      mutation: CANCEL_MEMBERSHIP_MUTATION,
      variables: {
        input: {
          userId: userStore.id
        }
      }
    })

    if (
      !data ||
      data.cancelMembership.__typename !== 'CancelMembershipOutput'
    ) {
      // toastsStore.pushToast({
      //   content: 'There was an error with that request',
      //   icon: 'exclamation-square',
      //   semantic: ToastSemantic.Error
      // })
    }

    emits('next')
  } catch (e) {
    console.error(e)
    // toastsStore.pushToast({
    //   content: 'There was an error with that request',
    //   icon: 'exclamation-square',
    //   semantic: ToastSemantic.Error
    // })
  }
}
 
const handleClaimPremiumMembership = async () => {
  loading.value = true
  try {
    const { data } = await client.mutate<updateUser>({
      mutation: UPDATE_USER,
      variables: {
        userId: userStore.id,
        golfMembershipId: 'complimentary-upgrade'
      }
    })

    // The API will return a server error when the entered Golf Canada Membership
    // ID does not exist.
    if (data?.updateUser.__typename !== 'UpdateUserOutput' || data.updateUser.user === null) {
      golfCanadaMembershipIdError.value = 'Golf Canada Membership ID is invalid'
    }

    // If the API will return a user with the membership field inactive if the Golf
    // Canada Membership ID is valid, but expired
    else if (!data.updateUser.user.membership?.active) {
      golfCanadaMembershipIdError.value = 'The Golf Canada Membership associated with this ID has expired'
    }

    // If the API returns a user with the membership field active, the user has claimed their
    // membership and we can move them forward
    else {
      userStore.membership.active = true
      emits('next')
    }

  } catch (e) {
    console.log(e)
    sendToast('There was an error with that request', ToastSemantic.Error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="register-step-4">
    <BackgroundWrapper :background="bgImage" />
    <SplitContent
      :sticky="true"
      top-offset="var(--player-header-height)"
      class="margin-top--5 margin-bottom--5"
    >
      <template #left>
        <div class="register-step-4__form-wrapper margin-bottom--5">
          <h1 class="heading--h3 margin-bottom--8">
            Step 4: Enjoy an upgrade on us!
          </h1>
          <p class="body--p1--regular">
            For a limited time, all new users will receive a complimentary upgrade to an UpSwing Premium Membership! Get ready for exclusive perks and member benefits:
          </p>
          <div class="register-step-4__icons">
            <div class="register-step-4__icon">
              <USIcon name="golf-club-bg" />
              <p class="register-step-4__icon-label body--p1--regular">
                Exclusive UpSwing events
              </p>
            </div>
            <div class="register-step-4__icon">
              <USIcon name="clock-bg" />
              <p class="body--p1--regular register-step-4__icon-label">
                Advanced booking
              </p>
            </div>
            <div class="register-step-4__icon">
              <USIcon name="star-bg" />
              <p class="body--p1--regular register-step-4__icon-label">
                Points on every play
              </p>
            </div>
            <div class="register-step-4__icon">
              <USIcon name="trophy-bg" />
              <p class="body--p1--regular register-step-4__icon-label">
                Leaderboard for invite-only events
              </p>
            </div>
          </div>
          <div class="margin-bottom--5 register-step-4__option-1">
            <form 
              :disabled="loading" 
              @submit.prevent="handleClaimPremiumMembership"
            >
              <!--<div class="margin-bottom--7">
                <h3 class="display--d2--semibold">Option 1:</h3>
                <p>Claim your complimentary Premium Membership with a valid Golf Canada Membership!</p>
              </div>-->
              <!--<USInputText
                v-model="golfCanadaMembershipId"
                :error="golfCanadaMembershipIdError"
                name="golfCanadaMembershipId"
                heading="Golf Canada Membership ID"
                class="margin-bottom--6"
                input-id="golfCanadaMembershipId"
                :required="true"
                input-type="text"
              />-->
              <div class="register-step-4__buttons margin-bottom--5">
                <div class="margin-right--7">
                  <USButton
                    is="button"
                    label="Let's Go!"
                    :disabled="loading"
                    @click="handleClaimPremiumMembership"
                  />
                </div>
                <!--
                <div>
                  <button type="button" @click="handleCancelMembership">
                    <USButton
                    type="inline"
                    label="I'll think about it"
                  />
                  </button>
                </div>-->
              </div>
            </form>
          </div>
          <!--
          <div class="margin-bottom--7">
            <h3 class="display--d2--semibold">Option 2:</h3>
            <p>Purchase a Premium Membership for only $19.95/year!</p>
          </div>
          <p class="display--d2--semibold margin-bottom--8">
            Enter Your Credit Card Details
          </p>
          <label>
            <div id="stripe-element"></div>
          </label>
          <div class="register-step-4__buttons">
            <div class="margin-right--7">
              <USButton
                is="button"
                icon="lock"
                label="Checkout Securely"
                @click="confirmPaymentIntent"
              />
            </div>
            <div>
              <USButton
                is="button"
                type="inline"
                label="I'll think about it"
                @click="handleCancelMembership"
              />
            </div>
          </div>-->

        </div>
      </template>
      <template #right>
        <div class="register-step-4__right">
          <USAvatar
            :src="userStore.profilePicture"
            :initials="generateInitials"
            size="370px"
          />
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.register-step-4 {
  color: var(--color--neutral-a1);

  &__icons {
    display: flex;
    flex-flow: row wrap;
    margin-bottom: var(--spacing--5);
  }

  &__icon {
    align-items: center;
    color: var(--color--primary-b1);
    display: flex;
    flex-flow: row;
    flex-basis: calc(45% - var(--grid--gutter-width));
    margin-top: var(--spacing--6);

    &:nth-child(2n) {
      margin-left: var(--grid--gutter-width);
    }
  }

  &__icon-label {
    color: var(--color--neutral-a1);
    margin-left: var(--spacing--8);
  }

  &__buttons {
    align-items: center;
    display: flex;
    flex-flow: row;
    justify-content: space-between;
    margin-top: var(--spacing--6);
  }

  &__right {
    display: flex;
    margin-top: var(--spacing--5);
    justify-content: center;

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }
  }
}

// :deep(.us-avatar) {
//   height: 370px;
//   width: 370px;
// }
:deep(.us-avatar__initials-label) {
  font-size: var(--font-size--z1);
}

:deep(.split-content__content) {
  flex-flow: column-reverse;

  @media (min-width: bp.$breakpoint--tablet) {
    flex-flow: row wrap;
  }
}
</style>
